import { ArrowSquareOut, DownloadSimple } from '@phosphor-icons/react';
import type { VaultFile, VaultLink, Vault } from '@mybusinessmate/schema';
import { vaultFileUrl } from '../../lib/deliverables-api';
import { fileMeta, storageText, vaultIcon } from '../../lib/vault-display';
import { Icon } from '../Icon';
import { useToast } from '../ui/toast';

const SIDE =
  'flex flex-col gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-widget) p-(--mbm-space-5-5) shadow-(--mbm-shadow-table-card)';
const TILE =
  'flex size-(--mbm-size-icon-tile-large) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-surface-tint text-text-primary';

function Heading({ id, title, count }: { id: string; title: string; count: number }) {
  return (
    <h2 id={id} className="text-heading-h3 text-text-primary">
      {title} <span className="text-text-tertiary">{count}</span>
    </h2>
  );
}

function RowText({ title, meta }: { title: string; meta: string }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5) text-left">
      <span className="truncate text-label-large text-text-primary">{title}</span>
      <span className="truncate text-body-xsmall text-text-tertiary">{meta}</span>
    </span>
  );
}

/** Brand files (Figma C03.4): each one downloads through a short-lived signed link. */
export function BrandFilesCard({ files }: { files: VaultFile[] }) {
  const toast = useToast();
  const download = async (file: VaultFile) => {
    try {
      const { url } = await vaultFileUrl(file.id, file.fieldId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Couldn’t download that file', description: 'Please try again.', tone: 'error' });
    }
  };
  return (
    <section aria-labelledby="files-title" className={`${SIDE} bg-surface-card`}>
      <Heading id="files-title" title="Brand files" count={files.length} />
      <ul className="flex flex-col gap-(--mbm-space-2-5)">
        {files.map((file) => (
          <li key={file.id}>
            <button
              type="button"
              aria-label={`Download ${file.title}`}
              onClick={() => void download(file)}
              className="flex w-full items-center gap-(--mbm-space-3) rounded-(--mbm-radius-xs) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <span className={TILE}>
                <Icon icon={vaultIcon(file, 'file')} size="md" />
              </span>
              <RowText title={file.title} meta={fileMeta(file)} />
              <span className="text-text-primary">
                <Icon icon={DownloadSimple} size="md" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Links (Figma C03.4): a Drive folder, the monthly reports. They open in a new tab. */
export function LinksCard({ links }: { links: VaultLink[] }) {
  return (
    <section aria-labelledby="links-title" className={`${SIDE} bg-surface-card`}>
      <Heading id="links-title" title="Links" count={links.length} />
      <ul className="flex flex-col gap-(--mbm-space-2-5)">
        {links.map((link) => (
          <li key={link.id}>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={`Open ${link.title}`}
              className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-xs) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <span className={TILE}>
                <Icon icon={vaultIcon(link, 'link')} size="md" />
              </span>
              <RowText title={link.title} meta={link.description ?? link.url} />
              <span className="text-text-primary">
                <Icon icon={ArrowSquareOut} size="md" />
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** How much of the plan's storage the vault's files use (Figma C03.4 "Storage"). */
export function StorageCard({ storage }: { storage: Vault['storage'] }) {
  const { text, ratio } = storageText(storage.usedBytes, storage.limitBytes);
  return (
    <section
      aria-labelledby="storage-title"
      className="flex flex-col gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-widget) bg-surface-sky-fade p-(--mbm-space-5-5)"
    >
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 id="storage-title" className="text-label-medium text-text-primary">
          Storage
        </h2>
        <span className="text-body-small text-text-tertiary">{text}</span>
      </div>
      <div
        role="progressbar"
        aria-label="Storage used"
        aria-valuemin={0}
        aria-valuemax={storage.limitBytes}
        aria-valuenow={Math.min(storage.usedBytes, storage.limitBytes)}
        className="h-(--mbm-space-2) w-full overflow-hidden rounded-(--mbm-radius-xs) bg-border-list"
      >
        <div className="h-full rounded-(--mbm-radius-xs) bg-brand-primary" style={{ width: `${ratio * 100}%` }} />
      </div>
    </section>
  );
}
